import React from 'react';
import screenshot from './assets/screenshot_more_features.png';

interface FeaturesProps {
  theme: 'light' | 'dark';
}

const Features: React.FC<FeaturesProps> = ({ theme }) => {
  const accentColor = "#00CCEB";
  
  const comparisonData: (string | React.ReactNode)[][] = [
    ["3 custom presets", <span><strong>Unlimited</strong> custom presets</span>],
    ["1 custom hz band config (up to 41 bands)", <span><strong>Unlimited</strong> custom hz band configs (each up to 41 bands)</span>],
    ["23, 27 and 33 band EQs", "23, 27 and 33 band EQs"],
    ["Ads removed", "Ads removed"],
    ["Decibel values visible and able to fine-tune", "Decibel values visible and able to fine-tune"],
    ["Color settings", "Color settings"],
    ["Lock the EQ (prevent accidental touches)", "Lock the EQ (prevent accidental touches)"],
    ["Reset and copying bands", "Reset and copying bands"],
    ["Mirroring of preset settings in Separate mode", "Mirroring of preset settings in Separate mode"],
    ["Use the unlocked app on all your compatible devices (where you are using your Google account)", "Use the premium app on all your compatible devices (where you are using your Google account)"],
    ["Features still available after uninstall/reinstall (any custom presets need to set and saved again)", "Features still available after uninstall/reinstall (any custom presets can be manually exported and imported after install)"],
    ["(Android:) Bypass or close the running EQ directly from the notification", "(Android:) Bypass or close the running EQ directly from the notification"],
    // Exclusive to subscribers (Index 13 starts here)
    ["", "Being able to export/import your presets"],
    ["", "Unlimited built-in Media player (playlists, podcasts, downloads and more)"],
    ["", "Auto-switch presets when using headphones or different sound sources"],
    ["", "100-1600 ms averaging Visualizer window"],
    ["", "Increased dB range on EQ bands, pre-gain and balance (v4.2)"],
    ["", "(Android:) Enable the EQ on app start"],
    ["", "(Android:) Auto-start the app on device boot"],
    ["", "(Android:) Fade-in volume when starting a new track (if the EQ takes some time before kicking in on your music app this can improve your experience)"],
    ["", "(Android:) Choose presets directly from the notification"],
    // Update index (currently 20)
    ["", "Audio Effects:"],
    ["", "Reverb"],
    ["", "Limiter"],
    ["", "Extra Bass boost"],
    ["", "Virtualizer/3D Sound (stereo expansion)"],
    ["", "Multi-band compressor (v4.2)"],
    ["", "(Android:) Auto-pan"],
  ];

  return (
    <div style={{ padding: "40px 15px", maxWidth: "900px", margin: "0 auto", lineHeight: "1.6" }}>
      <section style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h1 style={{ fontSize: '3rem', marginBottom: '20px' }}>Premium <span style={{ color: accentColor }}>Features</span></h1>
        <p style={{ fontSize: '1.1rem', maxWidth: '700px', margin: '0 auto' }}>
          Developing quality software takes many engineering hours and resources. SpotEQ31 is created by a small startup with no external funding. 
          If you get value from this app and like to support it to become the ultimate EQ experience on mobile devices, please consider unlocking one of the feature packs below (purchases are done inside the app in your local currency).

        </p>

        {/* App Screenshot */}
        <div style={{ 
          marginTop: '30px', 
          display: 'flex', 
          justifyContent: 'center',
          perspective: '1000px' 
        }}>
          <img 
            src={screenshot} 
            alt="SpotEQ31 Unlock Features" 
            style={{ 
              width: '100%', 
              maxWidth: '260px', 
              borderRadius: '25px', 
              boxShadow: theme === 'dark' 
                ? '0 15px 30px rgba(0, 204, 235, 0.2)'
                : '0 15px 30px rgba(0,0,0,0.15)',
              border: theme === 'dark' ? '6px solid #252525' : '6px solid #fff'
            }} 
          />
        </div>
      </section>

      <div className="comparison-box" style={{ 
        padding: '30px', 
        borderRadius: '20px', 
        background: theme === 'dark' ? '#1a1a1a' : '#f0f4f8',
        marginBottom: '60px'
      }}>
        <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>Feature Comparison</h2>

        <p style={{ textAlign: 'center', fontSize: '1.1rem', maxWidth: '700px', margin: '0 auto', marginBottom: '30px' }}>
            Some features are still Android only (marked below)
        </p>

        <div className="comparison-grid" style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '20px 30px', 
          fontSize: '0.95rem'
        }}>
          {/* Headers */}
          <h3 style={{ fontSize: '1.1rem', color: accentColor, marginBottom: '10px' }}>Basic (One-time fee)</h3>
          <h3 style={{ fontSize: '1.1rem', color: accentColor, marginBottom: '10px' }}>Subscription (Monthly fee)</h3>

          {/* Comparison Rows */}
          {comparisonData.map((row, index) => {
            const isHeader = row[1] === "Audio Effects:";
            const isAudioEffectItem = index > 21;
            const isSubscriberExclusiveStart = index === 12;
            const isExclusiveItem = row[0] === "";

            return (
              <React.Fragment key={index}>
                {isSubscriberExclusiveStart && (
                  <div style={{ 
                    gridColumn: '1 / span 2', 
                    textAlign: 'center', 
                    marginTop: '30px', 
                    marginBottom: '15px' 
                  }}>
                    <div style={{ 
                      fontWeight: 'bold', 
                      color: theme === 'dark' ? '#FFFFFF' : '#000000',
                      borderBottom: `2px solid ${accentColor}`,
                      display: 'inline-block',
                      paddingBottom: '5px'
                    }}>
                      Exclusive to subscribers:
                    </div>
                  </div>
                )}
                
                {isExclusiveItem ? (
                  /* Centered Exclusive Item spanning both columns */
                  <div style={{ 
                    gridColumn: '1 / span 2', 
                    display: 'flex', 
                    justifyContent: 'center' 
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      background: theme === 'dark' ? '#252525' : '#ffffff',
                      boxShadow: theme === 'dark' ? '0 2px 4px rgba(0,0,0,0.2)' : '0 2px 4px rgba(0,0,0,0.05)',
                      width: '100%',
                      maxWidth: '500px',
                      paddingLeft: isAudioEffectItem ? '40px' : '20px'
                    }}>
                      <span style={{ marginRight: '8px', color: accentColor }}>•</span>
                      <span style={{ fontWeight: isHeader ? 'bold' : 'normal' }}>{row[1]}</span>
                    </div>
                  </div>
                ) : (
                  /* Standard side-by-side items */
                  <>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start',
                      padding: '10px',
                      borderRadius: '8px',
                      background: row[0] ? (theme === 'dark' ? '#252525' : '#ffffff') : 'transparent',
                      boxShadow: row[0] ? (theme === 'dark' ? '0 2px 4px rgba(0,0,0,0.2)' : '0 2px 4px rgba(0,0,0,0.05)') : 'none'
                    }}>
                      {row[0] && <span style={{ marginRight: '8px', color: accentColor }}>•</span>}
                      <span>{row[0]}</span>
                    </div>
                    
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start',
                      padding: '10px',
                      borderRadius: '8px',
                      background: row[1] ? (theme === 'dark' ? '#252525' : '#ffffff') : 'transparent',
                      boxShadow: row[1] ? (theme === 'dark' ? '0 2px 4px rgba(0,0,0,0.2)' : '0 2px 4px rgba(0,0,0,0.05)') : 'none'
                    }}>
                      {row[1] && <span style={{ marginRight: '8px', color: accentColor }}>•</span>}
                      <span>{row[1]}</span>
                    </div>
                  </>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <footer style={{ fontSize: '0.8rem', opacity: 0.7, textAlign: 'center', borderTop: '1px solid #444', paddingTop: '20px' }}>
        <p>Your support helps us keep SpotEQ31 continuously improved. Thank you!</p>
      </footer>

      <style>{`
        @media (max-width: 600px) {
          .comparison-box {
            padding: 15px !important;
          }
          .comparison-grid {
            gap: 15px 10px !important;
            font-size: 0.85rem !important;
          }
          h1 {
            font-size: 2rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Features;
